import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Slide, Typography, useMediaQuery } from '@mui/material';
import FaceIcon from '@mui/icons-material/Face';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { biometrics } from '../platform/biometrics';
import notifications from '../platform/notifications';
import { nativeSetup } from '../platform/nativeSetup';

// A new account gets a fresh flow; late work from a previous account cannot advance it.
export default function NativeSetup({ userId }) {
    return <DeviceSetup key={userId} userId={userId} />;
}

// Mounted only for an authenticated native account after EULA acceptance.
function DeviceSetup({ userId }) {
    const [steps, setSteps] = useState([]);
    const [stepIndex, setStepIndex] = useState(0);
    const [status, setStatus] = useState(null);
    const [permission, setPermission] = useState('default');
    const [pushEnabled, setPushEnabled] = useState(false);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    const flowRef = useRef(null);
    const pendingRef = useRef(true);
    const headingRef = useRef(null);
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const stage = steps[stepIndex];
    const displayedStage = steps[Math.min(stepIndex, steps.length - 1)];

    useEffect(() => {
        const flow = { cancelled: false };
        flowRef.current = flow;
        const prepare = async () => {
            if (userId == null || await nativeSetup.wasShown(userId) || flow.cancelled) return;
            const [biometric, push] = await Promise.allSettled([
                biometrics.status(), notifications.getStatus()
            ]);
            if (flow.cancelled) return;
            const current = biometric.status === 'fulfilled' ? biometric.value : null;
            const pushStatus = push.status === 'fulfilled' ? push.value : null;
            await nativeSetup.markShown(userId);
            if (flow.cancelled) return;
            setStatus(current);
            setPermission(pushStatus?.permission || 'default');
            setSteps([
                ...(current?.available && !current.enabled ? ['biometric'] : []),
                ...(notifications.isSupported() && !pushStatus?.enabled ? ['push'] : [])
            ]);
        };
        void prepare().catch(() => {});
        return () => { flow.cancelled = true; };
    }, [userId]);

    // Refresh the system-permission explanation after returning from device settings.
    useEffect(() => {
        if (stage !== 'push') return undefined;
        const flow = flowRef.current;
        const refresh = async () => {
            if (document.visibilityState !== 'visible' || pendingRef.current) return;
            try {
                const current = await notifications.getStatus();
                if (!flow.cancelled && !pendingRef.current) setPermission(current.permission);
            } catch { /* The enable action retains its own retry/error handling. */ }
        };
        document.addEventListener('visibilitychange', refresh);
        return () => document.removeEventListener('visibilitychange', refresh);
    }, [stage]);

    const advance = () => {
        setError('');
        setStepIndex(index => index + 1);
    };
    const skip = () => {
        if (pendingRef.current) return;
        pendingRef.current = true;
        setBusy(true);
        advance();
    };
    const enable = async () => {
        if (pendingRef.current) return;
        const flow = flowRef.current;
        pendingRef.current = true;
        setBusy(true);
        setError('');
        try {
            if (stage === 'biometric') {
                await biometrics.enable();
            } else {
                // Retry only the test after a successful registration, never enrollment.
                const result = pushEnabled
                    ? (await notifications.sendTest(), { testAccepted: true })
                    : await notifications.enable({ sendTest: true });
                if (flow.cancelled) return;
                setPushEnabled(true);
                setPermission('granted');
                if (result?.testAccepted === false) {
                    setError('Notifications are enabled, but the test could not be sent. You can retry the test.');
                    pendingRef.current = false;
                    setBusy(false);
                    return;
                }
            }
            if (!flow.cancelled) advance();
        } catch (cause) {
            if (flow.cancelled) return;
            setError(stage === 'biometric'
                ? 'Biometric setup was cancelled or unavailable. Try again or choose Not now.'
                : cause.response?.data?.error || cause.message || 'Failed to enable push notifications. Try again or choose Not now.');
            pendingRef.current = false;
            setBusy(false);
        }
    };
    const onStepEntered = () => {
        pendingRef.current = false;
        setBusy(false);
        headingRef.current?.focus();
    };
    const BiometricIcon = /face/i.test(status?.label || '') ? FaceIcon : FingerprintIcon;
    const Icon = displayedStage === 'biometric' ? BiometricIcon : NotificationsIcon;
    const title = displayedStage === 'biometric' ? `Enable ${status?.label || 'biometric login'}?` : 'Enable push notifications?';

    return <Dialog open={Boolean(stage)} onClose={skip} maxWidth="xs" fullWidth
        aria-labelledby="device-setup-title" aria-describedby="device-setup-description">
        <DialogTitle id="device-setup-title">Set up this device</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden', p: 0 }}>
            <Slide key={displayedStage || 'loading'} direction="left" in={Boolean(displayedStage)} appear
                timeout={reduceMotion ? 0 : 240} onEntered={onStepEntered}>
                <Box sx={{ px: 3, pb: 1 }} aria-busy={busy}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Step {Math.min(stepIndex + 1, steps.length)} of {steps.length}
                    </Typography>
                    <Typography ref={headingRef} tabIndex={-1} variant="h6" component="h2"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, '&:focus': { outline: 'none' } }}>
                        <Icon color="primary" sx={{ fontSize: 28, flexShrink: 0 }} />
                        {title}
                    </Typography>
                    <DialogContentText id="device-setup-description">
                        {displayedStage === 'biometric'
                            ? 'Use face recognition or your fingerprint to sign in on this device. You can change this in your profile.'
                            : 'Receive important alerts on this device even when the app is closed. You can change this in your notification settings.'}
                    </DialogContentText>
                    {stage === 'push' && permission === 'denied' && <Alert severity="warning" sx={{ mt: 2 }}>
                        Push notifications are disabled for Connect App 365 in this device's system settings.
                        Enable notifications there, then return to the app and try again.
                    </Alert>}
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    <DialogActions sx={{ px: 0, pt: 2, '& .MuiButton-root': { minWidth: 48, minHeight: 48 },
                        '& .MuiButton-root:focus-visible': { outline: '2px solid currentColor', outlineOffset: 2 } }}>
                        <Button disabled={busy} onClick={skip}>Not now</Button>
                        <Button disabled={busy} onClick={enable}>{stage === 'push' && pushEnabled ? 'Retry test' : 'Enable'}</Button>
                    </DialogActions>
                </Box>
            </Slide>
        </DialogContent>
    </Dialog>;
}
